/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — Student Dashboard (Phase 9)
 *
 * Личный кабинет с аналитикой:
 *   Уровень 0 — Общая сводка
 *   Уровень 1 — Домены College Board
 *   Уровень 2 — Навыки внутри домена
 *   Рекомендации
 * ═══════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api-client';
import type {
    StudentOverview,
    DomainBreakdown,
    SkillBreakdown,
    Recommendation,
    PracticeTestHistory,
} from '../lib/types';

type Tab = 'training' | 'practice-tests';

export function StudentDashboard() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const studentId = searchParams.get('studentId') ?? 'demo-student';

    const [tab, setTab] = useState<Tab>('training');
    const [overview, setOverview] = useState<StudentOverview | null>(null);
    const [domains, setDomains] = useState<DomainBreakdown[]>([]);
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const [skills, setSkills] = useState<SkillBreakdown[]>([]);
    const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
    const [practiceHistory, setPracticeHistory] = useState<PracticeTestHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadTrainingAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [ov, dom, rec] = await Promise.all([
                api.getStudentOverview(studentId, 'training'),
                api.getDomainBreakdown(studentId, 'training'),
                api.getRecommendations(studentId, 'training'),
            ]);
            setOverview(ov);
            setDomains(dom.domains);
            setRecommendations(rec.recommendations);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [studentId]);

    const loadSkills = useCallback(async (domain: string) => {
        try {
            const result = await api.getSkillBreakdown(studentId, domain, 'training');
            setSkills(result.skills);
            setSelectedDomain(domain);
        } catch {
            // silently fail
        }
    }, [studentId]);

    const loadPracticeHistory = useCallback(async () => {
        setLoading(true);
        try {
            const result = await api.getPracticeTests(studentId);
            setPracticeHistory(result.tests);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
        }
    }, [studentId]);

    useEffect(() => {
        if (tab === 'training') {
            loadTrainingAnalytics();
        } else {
            loadPracticeHistory();
        }
    }, [tab, loadTrainingAnalytics, loadPracticeHistory]);

    const formatSeconds = (s: number): string => {
        if (s < 60) return `${s}s`;
        const mins = Math.floor(s / 60);
        const hours = Math.floor(mins / 60);
        if (hours > 0) return `${hours}h ${mins % 60}m`;
        return `${mins}m`;
    };

    const statusColor = (status: string) => {
        if (status === 'strong') return 'bg-green-100 text-green-700';
        if (status === 'adequate') return 'bg-yellow-100 text-yellow-700';
        return 'bg-red-100 text-red-700';
    };

    const accuracyColor = (rate: number) => {
        if (rate >= 0.8) return 'text-green-600';
        if (rate >= 0.6) return 'text-yellow-600';
        return 'text-red-600';
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-gray-500 text-lg">Loading analytics...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="mt-4 text-sat-blue underline"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="mx-auto max-w-5xl px-6 py-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">📊 My Dashboard</h1>
                        <p className="text-sm text-gray-500">Student ID: {studentId}</p>
                    </div>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                        ← Back
                    </button>
                </div>

                {/* Tabs */}
                <div className="mx-auto max-w-5xl px-6 flex gap-1 pb-0">
                    <button
                        onClick={() => setTab('training')}
                        className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${tab === 'training'
                            ? 'bg-white text-sat-blue border-b-2 border-sat-blue -mb-px'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        📈 Training Analytics
                    </button>
                    <button
                        onClick={() => setTab('practice-tests')}
                        className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${tab === 'practice-tests'
                            ? 'bg-white text-sat-blue border-b-2 border-sat-blue -mb-px'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        📝 Practice Tests History
                    </button>
                </div>
            </div>

            <div className="mx-auto max-w-5xl px-6 py-8 space-y-8">
                {tab === 'training' && (
                    <>
                        {/* Level 0: Overview */}
                        {overview && (
                            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Tests</p>
                                    <p className="mt-1 text-3xl font-bold text-gray-900">{overview.totalTests}</p>
                                </div>
                                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Questions</p>
                                    <p className="mt-1 text-3xl font-bold text-gray-900">{overview.totalQuestions}</p>
                                </div>
                                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Accuracy</p>
                                    <p className={`mt-1 text-3xl font-bold ${accuracyColor(overview.overallAccuracy)}`}>
                                        {Math.round(overview.overallAccuracy * 100)}%
                                    </p>
                                </div>
                                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Time</p>
                                    <p className="mt-1 text-3xl font-bold text-gray-900">
                                        {formatSeconds(overview.totalPracticeTimeSeconds)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Level 1: Domain Breakdown */}
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 mb-3">Domains</h2>
                            <div className="grid gap-3 md:grid-cols-2">
                                {domains.map((d) => (
                                    <button
                                        key={d.domainSlug}
                                        onClick={() => loadSkills(d.domainSlug)}
                                        className={`rounded-lg border p-4 text-left transition-all ${selectedDomain === d.domainSlug
                                            ? 'border-sat-blue ring-2 ring-sat-blue/20'
                                            : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold text-gray-800">{d.displayName}</span>
                                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(d.status)}`}>
                                                {d.status === 'strong' ? '✓' : d.status === 'adequate' ? '~' : '!'}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                            <span>{d.questionsAttempted} questions</span>
                                            <span className={accuracyColor(d.accuracyRate)}>
                                                {Math.round(d.accuracyRate * 100)}%
                                            </span>
                                            <span>{d.trend === 'up' ? '↑' : d.trend === 'down' ? '↓' : '→'}</span>
                                        </div>
                                        {/* Progress bar */}
                                        <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${d.accuracyRate >= 0.8 ? 'bg-green-400' : d.accuracyRate >= 0.6 ? 'bg-yellow-400' : 'bg-red-400'}`}
                                                style={{ width: `${Math.round(d.accuracyRate * 100)}%` }}
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Level 2: Skill Breakdown */}
                        {selectedDomain && skills.length > 0 && (
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 mb-3">
                                    Skills — {domains.find((d) => d.domainSlug === selectedDomain)?.displayName}
                                </h2>
                                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left font-semibold text-gray-700">Skill</th>
                                                <th className="px-4 py-2 text-center font-semibold text-gray-700">Q</th>
                                                <th className="px-4 py-2 text-center font-semibold text-gray-700">Accuracy</th>
                                                <th className="px-4 py-2 text-center font-semibold text-gray-700">Avg Time</th>
                                                <th className="px-4 py-2 text-right font-semibold text-gray-700"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {skills.map((s) => (
                                                <tr key={s.skillSlug} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <span className="font-medium text-gray-800">{s.displayName}</span>
                                                        <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs ${statusColor(s.status)}`}>
                                                            {s.status === 'strong' ? 'Strong' : s.status === 'adequate' ? 'OK' : 'Weak'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-gray-600">{s.questionsAttempted}</td>
                                                    <td className={`px-4 py-3 text-center font-semibold ${accuracyColor(s.accuracyRate)}`}>
                                                        {Math.round(s.accuracyRate * 100)}%
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-gray-600">{formatSeconds(s.averageTimeSeconds)}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            onClick={() => {
                                                                const params = new URLSearchParams({
                                                                    mode: 'training',
                                                                    studentId,
                                                                    topic: s.skillSlug,
                                                                    tier: s.status === 'needs_improvement' ? 'T1' : 'T2',
                                                                });
                                                                navigate(`/test?${params}`);
                                                            }}
                                                            className="rounded-md bg-sat-blue px-3 py-1 text-xs font-semibold text-white hover:bg-sat-blue-dark"
                                                        >
                                                            Train
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Recommendations */}
                        {recommendations.length > 0 && (
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 mb-3">💡 Recommendations</h2>
                                <div className="space-y-2">
                                    {recommendations.map((rec, i) => (
                                        <div
                                            key={`${rec.skillSlug}-${i}`}
                                            className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4"
                                        >
                                            <div>
                                                <p className="font-semibold text-blue-900">{rec.displayName}</p>
                                                <p className="text-sm text-blue-600">
                                                    {rec.reason === 'low_accuracy'
                                                        ? `Accuracy ${Math.round(rec.currentAccuracy * 100)}% — below 60% threshold`
                                                        : rec.reason === 'tier_upgrade'
                                                            ? `Accuracy ${Math.round(rec.currentAccuracy * 100)}% — ready for ${rec.recommendedTier}!`
                                                            : 'Negative trend detected'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const params = new URLSearchParams({
                                                        mode: 'training',
                                                        studentId,
                                                        topic: rec.skillSlug,
                                                        tier: rec.recommendedTier,
                                                    });
                                                    navigate(`/test?${params}`);
                                                }}
                                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                            >
                                                Start {rec.recommendedTier}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Practice Tests History Tab */}
                {tab === 'practice-tests' && (
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 mb-3">Practice Test History</h2>
                        {practiceHistory.length === 0 ? (
                            <p className="text-gray-500 py-8 text-center">
                                No practice tests completed yet. Start a Full Practice SAT or Spartan Mode test!
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {practiceHistory.map((test) => (
                                    <div
                                        key={test.testSessionId}
                                        className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold text-gray-800">
                                                {test.mode === 'spartan' ? '⚡ Spartan' : '📝 Full Practice'}
                                            </span>
                                            <span className="text-sm text-gray-500">{new Date(test.completedAt).toLocaleDateString()}</span>
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600">
                                            <span>Module 1: {test.module1Score}/{test.module1Total}</span>
                                            <span>Module 2 ({test.module2Track}): {test.module2Score}/{test.module2Total}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
